"use client"

import { useState } from "react"

// Colores personalizados
const colors = {
  gold: "#D4AF37",
  navyBlue: "#002366",
}

export default function LawyerManagement() {
  // Datos de ejemplo de abogados
  const [lawyers, setLawyers] = useState([
    {
      id: 1,
      name: "Carlos Rodríguez",
      email: "carlos@bufete.com",
      phone: "+34 612 345 678",
      specialization: "Contratos Mercantiles",
      licenseNumber: "45678",
      username: "carlos.rodriguez",
      status: "active",
      dateAdded: "2023-05-15",
    },
    {
      id: 2,
      name: "María González",
      email: "maria@bufete.com",
      phone: "+34 623 456 789",
      specialization: "Fusiones y Adquisiciones",
      licenseNumber: "56789",
      username: "maria.gonzalez",
      status: "active",
      dateAdded: "2023-06-22",
    },
    {
      id: 3,
      name: "Juan López",
      email: "juan@bufete.com",
      phone: "+34 634 567 890",
      specialization: "Propiedad Intelectual",
      licenseNumber: "67890",
      username: "juan.lopez",
      status: "inactive",
      dateAdded: "2023-04-10",
    },
    {
      id: 4,
      name: "Ana Martínez",
      email: "ana@bufete.com",
      phone: "+34 645 678 901",
      specialization: "Derecho Concursal",
      licenseNumber: "78901",
      username: "ana.martinez",
      status: "active",
      dateAdded: "2023-07-05",
    },
    {
      id: 5,
      name: "Pedro Sánchez",
      email: "pedro@bufete.com",
      phone: "+34 656 789 012",
      specialization: "Derecho Societario",
      licenseNumber: "89012",
      username: "pedro.sanchez",
      status: "inactive",
      dateAdded: "2023-03-18",
    },
  ])

  const [searchTerm, setSearchTerm] = useState("")
  const [newLawyer, setNewLawyer] = useState({
    name: "",
    email: "",
    phone: "",
    specialization: "",
    licenseNumber: "",
    username: "",
    password: "",
    confirmPassword: "",
    status: "active",
  })
  const [editingLawyer, setEditingLawyer] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLawyerId, setDeleteLawyerId] = useState(null)
  const [passwordError, setPasswordError] = useState("")

  // Filtrar abogados basado en el término de búsqueda
  const filteredLawyers = lawyers.filter(
    (lawyer) =>
      lawyer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lawyer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lawyer.specialization.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Generar nombre de usuario a partir del nombre completo
  const generateUsername = (fullName) => {
    if (!fullName) return ""
    const nameParts = fullName.toLowerCase().split(" ")
    if (nameParts.length >= 2) {
      return `${nameParts[0]}.${nameParts[nameParts.length - 1]}`
    }
    return nameParts[0]
  }

  // Manejar cambio de nombre y generar username
  const handleNameChange = (e) => {
    const name = e.target.value
    setNewLawyer({
      ...newLawyer,
      name,
      username: generateUsername(name),
    })
  }

  // Validar contraseñas
  const validatePasswords = () => {
    if (newLawyer.password !== newLawyer.confirmPassword) {
      setPasswordError("Las contraseñas no coinciden")
      return false
    }
    if (newLawyer.password.length < 6) {
      setPasswordError("La contraseña debe tener al menos 6 caracteres")
      return false
    }
    setPasswordError("")
    return true
  }

  // Crear un nuevo abogado
  const handleCreateLawyer = () => {
    if (!validatePasswords()) return

    const id = lawyers.length > 0 ? Math.max(...lawyers.map((c) => c.id)) + 1 : 1
    const today = new Date().toISOString().split("T")[0]

    // En una aplicación real, aquí se enviaría la contraseña hasheada al backend
    const newLawyerData = {
      ...newLawyer,
      id,
      dateAdded: today,
    }

    // Eliminar la confirmación de contraseña antes de guardar
    delete newLawyerData.confirmPassword

    setLawyers([...lawyers, newLawyerData])
    setNewLawyer({
      name: "",
      email: "",
      phone: "",
      specialization: "",
      licenseNumber: "",
      username: "",
      password: "",
      confirmPassword: "",
      status: "active",
    })
    setShowCreateModal(false)
  }

  // Actualizar un abogado existente
  const handleUpdateLawyer = () => {
    setLawyers(lawyers.map((lawyer) => (lawyer.id === editingLawyer.id ? editingLawyer : lawyer)))
    setEditingLawyer(null)
    setShowEditModal(false)
  }

  // Eliminar un abogado
  const handleDeleteLawyer = () => {
    setLawyers(lawyers.filter((lawyer) => lawyer.id !== deleteLawyerId))
    setDeleteLawyerId(null)
    setShowDeleteModal(false)
  }

  // Cambiar el estado de un abogado (activo/inactivo)
  const toggleLawyerStatus = (id) => {
    setLawyers(
      lawyers.map((lawyer) =>
        lawyer.id === id ? { ...lawyer, status: lawyer.status === "active" ? "inactive" : "active" } : lawyer,
      ),
    )
  }

  return (
    <div className="container mx-auto py-6 px-4">
      {/* Cabecera */}
      <div className="bg-[#002366] text-white p-4 rounded-t-lg">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Gestión de Abogados - Bufete Mercantil</h1>
            <p className="text-gray-200 mt-1">
              Administre los abogados del bufete, cree cuentas y actualice su información.
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#D4AF37] text-[#002366] px-4 py-2 rounded-md font-medium hover:bg-opacity-90 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M19 8l-2 2" />
              <path d="M17 6l2 2" />
            </svg>
            Nuevo Abogado
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="bg-white p-6 rounded-b-lg shadow-md border border-[#002366] border-opacity-20">
        {/* Barra de búsqueda */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              type="search"
              className="block w-full p-2 pl-10 text-sm border border-[#002366] border-opacity-30 rounded-lg focus:ring-[#D4AF37] focus:border-[#D4AF37]"
              placeholder="Buscar abogados..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Tabla de abogados */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#002366] divide-opacity-20">
            <thead className="bg-[#002366] bg-opacity-5">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Teléfono
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Especialidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Nº Colegiado
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold text-[#002366] uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#002366] divide-opacity-10">
              {filteredLawyers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No se encontraron abogados
                  </td>
                </tr>
              ) : (
                filteredLawyers.map((lawyer) => (
                  <tr key={lawyer.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#002366]">{lawyer.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lawyer.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lawyer.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lawyer.specialization}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lawyer.licenseNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lawyer.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                          lawyer.status === "active" ? "bg-[#D4AF37] text-[#002366]" : "bg-gray-200 text-gray-700"
                        }`}
                        onClick={() => toggleLawyerStatus(lawyer.id)}
                      >
                        {lawyer.status === "active" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setEditingLawyer(lawyer)
                          setShowEditModal(true)
                        }}
                        className="text-[#002366] hover:text-[#002366] hover:underline mr-3"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => {
                          setDeleteLawyerId(lawyer.id)
                          setShowDeleteModal(true)
                        }}
                        className="text-red-600 hover:text-red-800 hover:underline"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear abogado */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#D4AF37] p-4">
              <h3 className="text-lg font-medium text-[#002366]">Crear Nuevo Abogado</h3>
              <p className="text-sm text-gray-500">
                Complete la información para agregar un nuevo abogado y crear su cuenta.
              </p>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Nombre Completo</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={newLawyer.name}
                  onChange={handleNameChange}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Email</label>
                <input
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={newLawyer.email}
                  onChange={(e) => setNewLawyer({ ...newLawyer, email: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Teléfono</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={newLawyer.phone}
                  onChange={(e) => setNewLawyer({ ...newLawyer, phone: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Especialidad</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={newLawyer.specialization}
                  onChange={(e) => setNewLawyer({ ...newLawyer, specialization: e.target.value })}
                >
                  <option value="">Seleccione especialidad</option>
                  <option value="Contratos Mercantiles">Contratos Mercantiles</option>
                  <option value="Fusiones y Adquisiciones">Fusiones y Adquisiciones</option>
                  <option value="Derecho Societario">Derecho Societario</option>
                  <option value="Propiedad Intelectual">Propiedad Intelectual</option>
                  <option value="Derecho Concursal">Derecho Concursal</option>
                  <option value="Derecho Bancario">Derecho Bancario</option>
                  <option value="Competencia y Antimonopolio">Competencia y Antimonopolio</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Número de Colegiado</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={newLawyer.licenseNumber}
                  onChange={(e) => setNewLawyer({ ...newLawyer, licenseNumber: e.target.value })}
                />
              </div>

              <div className="border-t border-gray-200 my-4 pt-4">
                <h4 className="font-medium text-[#002366] mb-2">Información de Cuenta</h4>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#002366] mb-1">Nombre de Usuario</label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                    value={newLawyer.username}
                    onChange={(e) => setNewLawyer({ ...newLawyer, username: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se genera automáticamente a partir del nombre, pero puede modificarlo.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#002366] mb-1">Contraseña</label>
                  <input
                    type="password"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                    value={newLawyer.password}
                    onChange={(e) => setNewLawyer({ ...newLawyer, password: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#002366] mb-1">Confirmar Contraseña</label>
                  <input
                    type="password"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                    value={newLawyer.confirmPassword}
                    onChange={(e) => setNewLawyer({ ...newLawyer, confirmPassword: e.target.value })}
                  />
                  {passwordError && <p className="text-red-500 text-xs mt-1">{passwordError}</p>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#002366] mb-1">Estado</label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                    value={newLawyer.status}
                    onChange={(e) => setNewLawyer({ ...newLawyer, status: e.target.value })}
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-[#002366] text-[#002366] rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateLawyer}
                className="px-4 py-2 bg-[#002366] text-white rounded-md hover:bg-opacity-90"
              >
                Crear Cuenta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar abogado */}
      {showEditModal && editingLawyer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#D4AF37] p-4">
              <h3 className="text-lg font-medium text-[#002366]">Editar Abogado</h3>
              <p className="text-sm text-gray-500">Actualice la información del abogado.</p>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Nombre</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={editingLawyer.name}
                  onChange={(e) => setEditingLawyer({ ...editingLawyer, name: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Email</label>
                <input
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={editingLawyer.email}
                  onChange={(e) => setEditingLawyer({ ...editingLawyer, email: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Teléfono</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={editingLawyer.phone}
                  onChange={(e) => setEditingLawyer({ ...editingLawyer, phone: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Especialidad</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={editingLawyer.specialization}
                  onChange={(e) => setEditingLawyer({ ...editingLawyer, specialization: e.target.value })}
                >
                  <option value="Contratos Mercantiles">Contratos Mercantiles</option>
                  <option value="Fusiones y Adquisiciones">Fusiones y Adquisiciones</option>
                  <option value="Derecho Societario">Derecho Societario</option>
                  <option value="Propiedad Intelectual">Propiedad Intelectual</option>
                  <option value="Derecho Concursal">Derecho Concursal</option>
                  <option value="Derecho Bancario">Derecho Bancario</option>
                  <option value="Competencia y Antimonopolio">Competencia y Antimonopolio</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#002366] mb-1">Número de Colegiado</label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  value={editingLawyer.licenseNumber}
                  onChange={(e) => setEditingLawyer({ ...editingLawyer, licenseNumber: e.target.value })}
                />
              </div>

              <div className="border-t border-gray-200 my-4 pt-4">
                <h4 className="font-medium text-[#002366] mb-2">Información de Cuenta</h4>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#002366] mb-1">Nombre de Usuario</label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                    value={editingLawyer.username}
                    onChange={(e) => setEditingLawyer({ ...editingLawyer, username: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#002366] mb-1">Estado</label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                    value={editingLawyer.status}
                    onChange={(e) => setEditingLawyer({ ...editingLawyer, status: e.target.value })}
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
                <div className="mb-4">
                  <button
                    className="text-[#002366] text-sm underline"
                    onClick={() => alert("Funcionalidad para restablecer contraseña")}
                  >
                    Restablecer contraseña
                  </button>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-[#002366] text-[#002366] rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateLawyer}
                className="px-4 py-2 bg-[#002366] text-white rounded-md hover:bg-opacity-90"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para confirmar eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            <div className="p-4">
              <h3 className="text-lg font-medium text-[#002366]">¿Está seguro?</h3>
              <p className="text-sm text-gray-500 mt-2">
                Esta acción no se puede deshacer. Esto eliminará permanentemente al abogado y su cuenta de acceso al
                sistema.
              </p>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-[#002366] text-[#002366] rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteLawyer}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

